import React, { useState, useRef, useMemo, useEffect } from 'react';
import Pagination from 'rc-pagination';
import { Input, Table as ReactStrapTable } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SetLimitEntries, { defaultSelectOptions } from './SetLimitEntries';
import { ENG_LOCALE } from '../lib/constants';

/** Reusable Table Component For Project
 *
 * @param {boolean} headerFixed - to turn header stick to top
 * @param {any[] | Function<any[]>} data - data array to show in row
 * @param {number} total - total no of items in data if not given data length will be used
 * @param {boolean} autoSlice - auto slice data into chunks to be used for pagination or else will display data right through the data array
 * @param {Object} pagination - Prams to override pagination settings
 * @param {number} pagination.page - current page number
 * @param {number} pagination.pageSize - no of rows to show in table
 * @param {Function} pagination.onChange - function to call when pagination change
 * @param {Object} column - Object containing column prams
 * @param {string} column.key - unique key for each column
 * @param {string} column.dataIndex - key to display from data
 * @param {string} column.label - heading used for the column
 * @param {Function} column.sort - function to used for sorting if supplied
 * @param {Function} column.render - function to used to override how the column is rendered for data
 * @param {string} noDataText - to show custom message if no data
 *
 * @returns Table Component
 */

const defaultSettings = {
  pageSize: defaultSelectOptions,
};

export function Table(props) {
  const {
    column,
    data: dataFn,
    total,
    responsive,
    autoSlice,
    pagination: paginationProp,
    showPagination,
    settings,
    boxesData,
    noDataText,
    ...restprops
  } = props;
  const [data, setData] = useState([]);
  const [pagination, setPagination] = useState({
    page: paginationProp?.page ?? 1,
    pageSize: paginationProp?.pageSize ?? defaultSettings?.pageSize[0],
  });
  const ref = useRef({ sortstate: {} });

  async function updateData() {
    if (typeof dataFn === 'function') {
      const data = await dataFn(pagination);
      setData(data);
    } else {
      setData(dataFn);
    }
  }

  useEffect(() => {
    if (dataFn) updateData();
  }, [dataFn, pagination]);

  useEffect(() => {
    if (paginationProp?.onChange) paginationProp.onChange(pagination);
  }, [pagination]);

  const tableData = useMemo(() => {
    if (autoSlice) {
      return data?.slice(
        (pagination.page - 1) * pagination?.pageSize,
        (pagination.page - 1) * pagination?.pageSize + pagination?.pageSize,
      );
    } else return data;
  }, [data, pagination, autoSlice]);

  return (
    <div>
      <div className={props.headerFixed && `table-header-fixed`}>
        <ReactStrapTable responsive={responsive ?? true} {...restprops}>
          <thead>
            <tr>
              {column?.map(col => {
                const { sortstate } = ref.current;
                return generateHeadCell(col, sortstate);
              })}
            </tr>
          </thead>
          <tbody>
            {tableData?.length ? (
              generateRow(tableData, column)
            ) : (
              <tr>
                <td className="text-center py-5" colSpan={column?.length}>
                  {noDataText || `No Data To Display In Table`}
                </td>
              </tr>
            )}
          </tbody>
        </ReactStrapTable>
      </div>
      {!showPagination && (
        <div className="row justify-content-center">
          <div className="col-auto">
            <Pagination
              locale={ENG_LOCALE}
              onChange={e => setPagination({ page: e, pageSize: pagination?.pageSize })}
              current={pagination?.page}
              total={data.length}
              pageSize={pagination?.pageSize}
              showLessItems
              showTitle={false}
              showTotal={(totalNum, range) => `${range[0]} - ${range[1]} of ${totalNum} items`}
            />
            <SetLimitEntries
              onChange={val => setPagination({ page: 1, pageSize: parseInt(val) })}
              value={pagination?.pageSize}
            />
          </div>
          <div className="col-auto">
            <Input
              type="select"
              onChange={e => setPagination({ page: 1, pageSize: parseInt(e.target.value) })}
              defaultValue={pagination?.pageSize}
            >
              {settings?.pageSize?.map(option => (
                <option key={option} value={option}>
                  {option} Rows
                </option>
              ))}
            </Input>
          </div>
        </div>
      )}
    </div>
  );

  function sort(col) {
    const prevdata = [...data];
    const { sortstate } = ref.current;
    const order =
      sortstate.key !== col.key || sortstate.order === 'default'
        ? 'assend'
        : sortstate.order === 'assend'
        ? 'decend'
        : 'default';
    const newdata =
      order === 'default'
        ? dataFn
        : prevdata.sort((a, b) => col.sort(a, b) * (order === 'assend' ? 1 : -1));

    setData(newdata);

    sortstate.key = col.key;
    sortstate.order = order;
  }

  function getCountReducer(currentComp) {
    return boxesData?.reduce((acc, el) => {
      if (el.assignedTo === currentComp) {
        return acc + 1;
      }
      return acc;
    }, 0);
  }

  function generateHeadCell(col, sortstate) {
    return (
      <th
        key={col.key ?? col.dataIndex ?? col.label}
        onClick={() => col.sort && sort(col)}
        className={`${col.sort && 'onhover-bgprimary cursor-pointer'}`}
      >
        {col.label}
        {col.sort && ( // if we have sort function for this column
          <span className="ml-2">
            {sortstate.key === col.key && sortstate.order === 'assend' ? (
              <FontAwesomeIcon icon="sort-down" /> // Assending icon
            ) : sortstate.key === col.key && sortstate.order === 'decend' ? (
              <FontAwesomeIcon icon="sort-up" /> // Decending icon
            ) : (
              <FontAwesomeIcon icon="sort" /> // Default icon
            )}
          </span>
        )}
      </th>
    );
  }

  function generateRow(data, column) {
    return data?.map((datum, rowindex) => {
      if (boxesData) {
        const count = getCountReducer(datum._id);
        datum.totalBoxes = datum.totalBoxes || count;
      }
      return (
        <tr key={rowindex + 1}>
          {column?.map(col => (
            <td key={`${col.key ?? col.dataIndex ?? col.label}-${rowindex + 1}`}>
              {col.render?.(datum[col.dataIndex], datum, rowindex) ?? datum[col.dataIndex]}
            </td>
          ))}
        </tr>
      );
    });
  }
}

export default Table;
